<template>
  <div class="employee-calendar pt-2 md:p-4 mt-0">
    <div class="flex justify-content-center">
      <div class="col-12">
        <!-- Selector de año -->
        <div
          class="customCardBlue w-full h-5rem flex align-items-center justify-content-center mb-4"
        >
          <div
            class="w-6 flex justify-content-between align-items-center gap-3"
          >
            <Button
              icon="pi pi-chevron-left"
              @click="previousYear"
              :disabled="loading"
              text
              rounded
            />
            <h2 class="text-2xl text-primary font-semibold m-0">
              {{ currentYear }}
            </h2>
            <Button
              icon="pi pi-chevron-right"
              @click="nextYear"
              :disabled="loading"
              text
              rounded
            />
          </div>
        </div>

        <!-- Calendarios de 12 meses -->
        <div v-if="!loading" class="grid">
          <div
            v-for="month in 12"
            :key="month"
            class="col-12 md:col-6 lg:col-4 xl:col-3"
          >
            <div class="surface-card border-round mb-0 calendar-card">
              <!-- Header personalizado con el nombre del mes -->
              <div class="datepicker-header-custom p-3">
                {{ getMonthName(month) }}
              </div>
              <div class="p-1">
                <DatePicker
                  v-model="selectedDates[month - 1]"
                  inline
                  :showOtherMonths="false"
                  :min-date="getMonthStart(currentYear, month)"
                  :max-date="getMonthEnd(currentYear, month)"
                  :disabled-dates="disabledDates"
                  :pt="datePickerPt"
                  @date-select="onDateSelect"
                  @update:model-value="onModelValueUpdate($event, month - 1)"
                >
                  <template #date="slotProps">
                    <div
                      :class="[getDayClass(slotProps.date), 'calendar-day-fixed']"
                      class="custom-day"
                    >
                      {{ slotProps.date.day }}
                    </div>
                  </template>
                </DatePicker>
              </div>
            </div>
          </div>
        </div>

        <!-- Skeleton de carga -->
        <div v-else class="grid">
          <div
            v-for="month in 12"
            :key="month"
            class="col-12 md:col-6 lg:col-4 xl:col-3"
          >
            <div class="surface-card border-round mb-0 calendar-card">
              <div class="datepicker-header-custom p-3">
                <Skeleton height="2rem" width="100%" borderRadius="8px" />
              </div>
              <div class="p-1">
                <div class="calendar-skeleton-grid">
                  <Skeleton v-for="i in 35" :key="i" height="1.7rem" width="1.7rem" borderRadius="6px" class="calendar-skeleton-day calendar-day-fixed" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Leyenda -->
        <div
          class="p-4 border-round my-4"
          style="
            border: 1px solid #e2e8f0;
            border-radius: 6px !important;
            background-color: #f6f6f6;
          "
        >
          <div
            class="flex align-items-center justify-content-start md:justify-content-start lg:justify-content-center flex-wrap gap-4 md:gap-4 lg:gap-4"
          >
            <div class="flex align-items-center gap-2">
              <div class="legend-box pending shadow-1"></div>
              <span>{{
                t("wkt.client.employeeCalendar.pending_request")
              }}</span>
            </div>

            <div class="flex align-items-center gap-2">
              <div class="legend-box approved shadow-1"></div>
              <span>{{
                t("wkt.client.employeeCalendar.approved_request")
              }}</span>
            </div>
            <div class="flex align-items-center gap-2">
              <div class="legend-box vacation shadow-1"></div>
              <span>{{ t("wkt.client.employeeCalendar.vacation") }}</span>
            </div>

            <div class="flex align-items-center gap-2">
              <div class="legend-box paid-absence shadow-1"></div>
              <span>{{ t("wkt.client.employeeCalendar.paidAbsence") }}</span>
            </div>

            <div class="flex align-items-center gap-2">
              <div class="legend-box unpaid-absence shadow-1"></div>
              <span>{{ t("wkt.client.employeeCalendar.unpaidAbsence") }}</span>
            </div>

            <div class="flex align-items-center gap-2">
              <div class="legend-box rest shadow-1"></div>
              <span>{{ t("wkt.client.employeeCalendar.rest") }}</span>
            </div>

            <div class="flex align-items-center gap-2">
              <div class="legend-box holiday shadow-1"></div>
              <span>{{ t("wkt.client.employeeCalendar.holiday") }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Componentes auxiliares -->
    <LoadingDialog ref="loadingDialogRef" />
    <EmployeeCalendarDetailDialog
      ref="detailDialog"
      @closed="onDetailDialogClosed"
    />
  </div>
</template>

<script setup lang="ts">
import { useAlert } from "@wkt/composables/useAlert";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  CalendarDayData,
  CalendarDayType,
  EmployeeCalendar,
  employeeCalendarAPI,
  FullVacation,
  ProcedureVacationGroup,
  RequestStatus,
} from "../../../api/employee-calendar";
import { useCalendar } from "../../../composables/useCalendar";
import LoadingDialog from "../../common/LoadingDialog.vue";
import EmployeeCalendarDetailDialog from "./EmployeeCalendarDetailDialog.vue";

// Composables
const { t } = useI18n();
const { showAlert } = useAlert();
const { getLongMonthName, isToday, formatDateKey, getFirstDayOfMonth, getLastDayOfMonth } = useCalendar();

// ============================================
// REACTIVE STATE
// ============================================
const currentYear = ref<number>(new Date().getFullYear());
const loading = ref<boolean>(false);
const selectedDates = ref<(Date | null)[]>(Array(12).fill(null));
const calendarData = ref<EmployeeCalendar[]>([]);
const vacationsData = ref<FullVacation[]>([]);
const selectedDayDetails = ref<CalendarDayData | null>(null);
const detailDialog = ref<InstanceType<
  typeof EmployeeCalendarDetailDialog
> | null>(null);
const disabledDates = ref<Date[]>([]);

// Configuración de estilo para DatePicker
const datePickerPt = {
  root: { class: "custom-calendar" },
  panel: { class: "p-0" },
  table: { class: "w-full" },
};

// ============================================
// COMPUTED
// ============================================

/**
 * Mapa de vacaciones por fecha para acceso rápido
 */
const vacationsByDate = computed(() => {
  const map = new Map<string, FullVacation[]>();

  vacationsData.value.forEach((vacation) => {
    if (vacation.fromDate && vacation.toDate) {
      const fromDate = new Date(
        vacation.fromDate.year,
        vacation.fromDate.month - 1,
        vacation.fromDate.day
      );
      const toDate = new Date(
        vacation.toDate.year,
        vacation.toDate.month - 1,
        vacation.toDate.day
      );

      // Agregar todas las fechas en el rango
      for (
        let d = new Date(fromDate);
        d <= toDate;
        d.setDate(d.getDate() + 1)
      ) {
        const key = formatDateKey(d);
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key)!.push(vacation);
      }
    }
  });

  return map;
});

/**
 * Mapa de datos del calendario por fecha
 */
const calendarByDate = computed(() => {
  const map = new Map<string, EmployeeCalendar>();

  calendarData.value.forEach((day) => {
    if (day.calendarDay) {
      const date = new Date(day.calendarDay);
      const key = formatDateKey(date);
      map.set(key, day);
    }
  });

  return map;
});

// ============================================
// MÉTODOS
// ============================================

async function loadYearData(): Promise<void> {
  loading.value = true;

  try {
    // Crear rango de fechas para el año completo
    const dateRange = employeeCalendarAPI.createYearDateRange(
      currentYear.value
    );

    // Cargar datos del calendario
    await new Promise<void>((resolve, reject) => {
      employeeCalendarAPI.getCalendar(
        dateRange,
        (data) => {
          calendarData.value = data;
          resolve();
        },
        (error) => {
          showAlert({
            text:
              error.message ||
              `Error al cargar calendario del año ${currentYear.value}`,
            color: "error",
          });
          reject(error);
        }
      );
    });

    // Cargar vacaciones para todo el año en una sola petición
    await new Promise<void>((resolve, reject) => {
      employeeCalendarAPI.getMyVacationYear(
        currentYear.value,
        (data: ProcedureVacationGroup) => {
          // Combinar vacaciones confirmadas + solicitudes pendientes + solicitudes aprobadas
          const combined: FullVacation[] = [];

          if (data.vacations) {
            combined.push(...data.vacations);
          }

          // Convertir FullProcedureRequest a FullVacation para solicitudes pendientes
          if (data.pendingRequests) {
            const converted = data.pendingRequests.map(
              (req: any) =>
                ({
                  id: req.id,
                  employeeId: req.employeeId,
                  firstname: req.firstname,
                  lastname: req.lastname,
                  fromDate: req.fromDate,
                  toDate: req.toDate,
                  fromHour: req.fromHour
                    ? `${req.fromHour.hours}:${String(req.fromHour.minutes).padStart(2, "0")}`
                    : undefined,
                  toHour: req.toHour
                    ? `${req.toHour.hours}:${String(req.toHour.minutes).padStart(2, "0")}`
                    : undefined,
                  recordStatus: 0, // PENDING
                  recordType: req.recordType || 0,
                  recordTitle: req.requestTitle || req.recordTitle,
                  recordInfo: req.requestDescription,
                  requestInfo: req.requestInfo,
                  hours: req.hours,
                  days: req.days,
                  hoursBagType: req.hoursBagType,
                  hoursBagName: req.hoursBagName,
                  hoursBagDelta: req.hoursBagDelta,
                  requestId: req.id,
                  requestStatus: req.requestStatus,
                  profilePicture: req.profilePicture,
                }) as FullVacation
            );
            combined.push(...converted);
          }

          // Convertir FullProcedureRequest a FullVacation para solicitudes aprobadas
          if (data.approvedRequests) {
            const converted = data.approvedRequests.map(
              (req: any) =>
                ({
                  id: req.id,
                  employeeId: req.employeeId,
                  firstname: req.firstname,
                  lastname: req.lastname,
                  fromDate: req.fromDate,
                  toDate: req.toDate,
                  fromHour: req.fromHour
                    ? `${req.fromHour.hours}:${String(req.fromHour.minutes).padStart(2, "0")}`
                    : undefined,
                  toHour: req.toHour
                    ? `${req.toHour.hours}:${String(req.toHour.minutes).padStart(2, "0")}`
                    : undefined,
                  recordStatus: 1, // APPROVED
                  recordType: req.recordType || 0,
                  recordTitle: req.requestTitle || req.recordTitle,
                  recordInfo: req.requestDescription,
                  requestInfo: req.requestInfo,
                  hours: req.hours,
                  days: req.days,
                  hoursBagType: req.hoursBagType,
                  hoursBagName: req.hoursBagName,
                  hoursBagDelta: req.hoursBagDelta,
                  requestId: req.id,
                  requestStatus: req.requestStatus,
                  profilePicture: req.profilePicture,
                }) as FullVacation
            );
            combined.push(...converted);
          }

          vacationsData.value = combined;
          resolve();
        },
        (error) => {
          showAlert({
            text:
              error.message ||
              `Error al cargar vacaciones del año ${currentYear.value}`,
            color: "error",
          });
          reject(error);
        }
      );
    });
  } catch (error) {
    showAlert({
      text: `Error al cargar datos del año: ${error}`,
      color: "error",
    });
  } finally {
    loading.value = false;
  }
}

function previousYear(): void {
  currentYear.value--;
  vacationsData.value = [];
  selectedDates.value = Array(12).fill(null);
  loadYearData();
}

/**
 * Obtiene el nombre del mes en español
 */
function getMonthName(monthNumber: number): string {
  return getLongMonthName(monthNumber);
}

/**
 * Navega al año siguiente
 */
function nextYear(): void {
  currentYear.value++;
  vacationsData.value = [];
  selectedDates.value = Array(12).fill(null);
  loadYearData();
}

function getMonthStart(year: number, month: number): Date {
  return getFirstDayOfMonth(month, year);
}

function getMonthEnd(year: number, month: number): Date {
  return getLastDayOfMonth(month, year);
}

function isDateSelectable(date: Date): boolean {
  const key = formatDateKey(date);
  const vacations = vacationsByDate.value.get(key) || [];
  return vacations.length > 0;
}

function getDayClass(dateObj: {
  day: number;
  month: number;
  year: number;
}): string {
  // Convertir el objeto de PrimeVue a Date de JavaScript
  // Nota: month en PrimeVue es 0-indexed (0 = enero, 11 = diciembre)
  const date = new Date(dateObj.year, dateObj.month, dateObj.day);
  const key = formatDateKey(date);
  const vacations = vacationsByDate.value.get(key) || [];
  const calendar = calendarByDate.value.get(key);

  const classes: string[] = [];

  // Marcar días no seleccionables
  if (!isDateSelectable(date)) {
    classes.push("day-not-selectable");
  }

  // PRIORIDAD 1: Verificar si tiene vacaciones o solicitudes (MÁXIMA PRIORIDAD)
  // Las vacaciones y solicitudes deben prevalecer sobre festivos y descansos
  if (vacations.length > 0) {
    const vacation = vacations[0];

    if (vacation.recordStatus === RequestStatus.PENDING) {
      // Solicitud pendiente (amarillo)
      classes.push("day-pending");
    } else if (vacation.recordStatus === RequestStatus.APPROVED) {
      // Solicitud aprobada (verde)
      classes.push("day-approved");
    } else if (vacation.hoursBagType ===  RequestStatus.PAID_ABSENCE) {
      // Ausencia retribuida (rosa)
      classes.push("day-paid-absence");
    } else if (vacation.hoursBagType === RequestStatus.VACATION) {
      // Vacaciones (azul)
      classes.push("day-vacation");
    } else if (vacation.hoursBagType !== RequestStatus.VACATION) {
      // Otras ausencias no retribuidas (púrpura)
      classes.push("day-unpaid-absence");
    }

    // Agregar clases adicionales para domingos y hoy, pero no weekend para mantener prioridad de colores
    if (date.getDay() === 0) {
      classes.push("day-sunday");
    }
    if (isToday(date)) {
      classes.push("day-today");
    }
    return classes.join(" ");
  }

  // PRIORIDAD 2: Verificar si es festivo o descanso (solo si NO hay vacaciones)
  if (calendar && calendar.dayType !== undefined) {
    if (
      calendar.dayType === CalendarDayType.NATIONAL_HOLIDAY ||
      calendar.dayType === CalendarDayType.LOCAL_HOLIDAY ||
      calendar.dayType === CalendarDayType.COMPANY_HOLIDAY
    ) {
      // Festivo (naranja claro)
      classes.push("day-holiday");

      if (date.getDay() === 0) {
        classes.push("day-sunday");
      }
      if (isToday(date)) {
        classes.push("day-today");
      }
      return classes.join(" ");
    }

    if (calendar.dayType === CalendarDayType.REST_DAY) {
      // Descanso (verde claro)
      classes.push("day-rest");

      if (date.getDay() === 0) {
        classes.push("day-sunday");
      }
      if (isToday(date)) {
        classes.push("day-today");
      }
      return classes.join(" ");
    }
  }

  // Verificar si es domingo (0 = domingo)
  if (date.getDay() === 0) {
    classes.push("day-sunday");
  }

  // Verificar si es hoy
  const today = new Date();
  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    classes.push("day-today");
  }

  return classes.join(" ");
}

/**
 * Maneja la actualización del modelo para prevenir selección de fechas no válidas
 */
function onModelValueUpdate(
  value: Date | Date[] | (Date | null)[] | null | undefined,
  monthIndex: number
): void {
  // Si el valor es una fecha simple y no es seleccionable, resetear
  if (value instanceof Date && !isDateSelectable(value)) {
    selectedDates.value[monthIndex] = null;
  }
}

/**
 * Maneja la selección de una fecha
 */
function onDateSelect(date: Date): void {
  // Solo permitir seleccionar fechas con vacaciones o solicitudes
  if (!isDateSelectable(date)) {
    // Prevenir la selección
    return;
  }

  const key = formatDateKey(date);
  const vacations = vacationsByDate.value.get(key) || [];
  const calendar = calendarByDate.value.get(key);

  selectedDayDetails.value = {
    date,
    employeeCalendar: calendar || null,
    vacations,
    isWeekend: date.getDay() === 0 || date.getDay() === 6,
    isToday: isToday(date),
    hasVacation: vacations.length > 0,
  };

  detailDialog.value?.open(selectedDayDetails.value);
}

function onDetailDialogClosed() {
  // Limpiar datos cuando se cierra el diálogo
  selectedDayDetails.value = null;
}

// ============================================
// LIFECYCLE
// ============================================

onMounted(() => {
  loadYearData();
});

// ============================================
// EXPOSED METHODS
// ============================================

function refreshModule(): void {
  // Resetear el año al actual
  currentYear.value = new Date().getFullYear();
  // Limpiar datos previos
  vacationsData.value = [];
  selectedDates.value = Array(12).fill(null);
  // Recargar los datos
  loadYearData();
}

// Exponer el método para que pueda ser llamado desde App.vue
defineExpose({
  refreshModule,
});
</script>

<style scoped>
.calendar-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  padding: 0.5rem 0.25rem 0.25rem 0.25rem;
  min-height: 12rem;
}

.calendar-skeleton-day {
  margin: 0 auto;
}
.employee-calendar {
  max-width: 100%;
  min-height: 100vh;
}
.customCardBlue {
  box-shadow: none;
  background-color: #e7eef6;
  border-radius: 8px;
  border: 1px solid #b8cfdd;
}

.customCardBlue .p-button-text {
  color: #185ea9 !important;
}

.customCardBlue .p-button-text:not(:disabled):hover {
  background-color: #d0e1f2 !important;
}

/* Oculta completamente todos los elementos del header de PrimeVue */
.custom-calendar .p-datepicker-header,
.custom-calendar :deep(.p-datepicker-header) {
  display: none !important;
  visibility: hidden !important;
  height: 0 !important;
  padding: 0 !important;
  margin: 0 !important;
  position: absolute !important;
}

/* También oculta los botones y texto del header */
.custom-calendar .p-datepicker-prev,
.custom-calendar .p-datepicker-next,
.custom-calendar .p-datepicker-title {
  display: none !important;
}

/* Personaliza el header del DatePicker para mostrar solo el mes */
.datepicker-header-custom {
  font-weight: 600;
  font-size: 1.2rem;
  text-align: center;
  color: #000000;
  width: 100%;
  display: block;
  margin: 0;
  padding: 1rem !important;
}

.calendar-card {
  padding: 0 !important;
  overflow: hidden;
}

.custom-calendar {
  width: 100%;
  min-height: 310px;
}

.custom-day {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 !important;
  cursor: pointer;
  transition: all 0.2s;
}

.custom-day:hover {
  transform: scale(1.1);
}

/* Prevenir cualquier estilo de selección de PrimeVue en todos los días */
:deep(.p-datepicker-day.p-datepicker-day-selected) {
  box-shadow: none !important;
  background-color: none !important;
}

:deep(.p-datepicker-day.p-datepicker-day-selected:hover) {
  background-color: none !important;
}

/* Días no seleccionables - prevenir interacción pero mantener apariencia normal */
:deep(.p-datepicker-day:has(.day-not-selectable)) {
  cursor: default;
  pointer-events: none !important;
}

/* Permitir eventos en el contenido interno para que funcionen los hovers y colores */
:deep(.p-datepicker-day .custom-day) {
  pointer-events: all !important;
}

.day-pending {
  background-color: #FEE8A1 !important; /* AMBER_ACCENT_2 - Solicitud pendiente (amarillo) */
  color: #000 !important;
  font-weight: bold;
}

.day-approved {
  background-color: #A7D9D2 !important; /* TEAL_LIGHTEN_4 - Solicitud aprobada (verde) */
  color: #000;
  font-weight: bold;
}

.day-paid-absence {
  background-color: #F9DDE8 !important; /* PINK_LIGHTEN_4 - Ausencia retribuida (rosa) */
  color: #000;
  font-weight: bold;
}

.day-vacation {
  background-color: #AFD6FB !important; /* BLUE_LIGHTEN_4 - Vacaciones (azul) */
  color: #000;
  font-weight: bold;
}

.day-unpaid-absence {
  background-color: #C5B6E5 !important; /* DEEP_PURPLE_LIGHTEN_4 - Otras ausencias no retribuidas (púrpura) */
  color: #000;
  font-weight: bold;
}

.day-rest {
  background-color: #E4F3E3 !important; /* Descanso (verde claro) */
  color: #000;
  font-weight: bold;
}

.day-holiday {
  background-color: #FDD8A1 !important; /* Festivo (amarillo claro) */
  color: #000;
  font-weight: bold;
}

.day-sunday {
  color: red !important;
  font-weight: bold;
}

.day-today {
  background-color: #185EA9;
  border-radius: 12px !important;
  color: white;
  font-weight: bold;
}

.legend-box {
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

.legend-box.pending {
  background-color: #FEE8A1; /* AMBER_ACCENT_2 - Solicitud pendiente (amarillo) */
}

.legend-box.approved {
  background-color: #A7D9D2; /* TEAL_LIGHTEN_4 - Solicitud aprobada (verde) */
}

.legend-box.paid-absence {
  background-color: #F9DDE8; /* PINK_LIGHTEN_4 - Ausencia retribuida (rosa) */
}

.legend-box.vacation {
  background-color: #AFD6FB; /* BLUE_LIGHTEN_4 - Vacaciones (azul) */
}

.legend-box.unpaid-absence {
  background-color: #C5B6E5; /* DEEP_PURPLE_LIGHTEN_4 - Otras ausencias no retribuidas (púrpura) */
}

.legend-box.rest {
  background-color: #E4F3E3; /* Descanso (verde claro) */
}

.legend-box.holiday {
  background-color: #FDD8A1; /* Festivo (amarillo claro) */
}


/* Fuerza el mismo ancho para todas las celdas de la tabla de días del DatePicker PrimeVue */
.custom-calendar :deep(.p-datepicker-calendar td) {
  min-width: 2.5rem !important;
  max-width: 2.5rem !important;
  width: 3.5rem !important;
  padding: 0 !important;
  box-sizing: border-box;
}

/* Fuerza el mismo ancho y alto para todas las celdas de día del calendario */
.calendar-day-fixed {
  min-width: 3.5rem !important;
  max-width: 3.5rem !important;
  width: 3.5rem !important;
  min-height: 3rem !important;
  max-height: 3rem !important;
  height: 3rem !important;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>